import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Title,
  Button,
  Input,
  Select,
  Option,
  Label,
  TextArea,
  BusyIndicator,
  MessageStrip,
  Icon,
  Text
} from '@ui5/webcomponents-react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import axiosInstance from '../../utils/axiosInstance';
import useAutoTranslate from '../../hooks/useAutoTranslate';

const scheduleTypes = [
  { id: 'DELIVERY', name: 'Despacho' },
  { id: 'PICKUP', name: 'Retiro' }
];

const AppointmentModal = ({ isOpen, onClose, orderNumber, purchaseOrderId, deliveryDate, deliveryAddress }) => {
  const { autoT } = useAutoTranslate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [events, setEvents] = useState([]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [lastFetchedRange, setLastFetchedRange] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [createdAppointmentNumber, setCreatedAppointmentNumber] = useState('');
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [formData, setFormData] = useState({
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    scheduleAdress: deliveryAddress || '',
    isDelivery: 'DELIVERY',
    orderNumber: orderNumber || '',
    specialInstructions: '',
    purchaseOrderId: purchaseOrderId || '',
    scheduleDate: null,
    deliveryPickupDate: null,
    Status: 'PENDING',
    createdBy: null,
  });

  useEffect(() => {
    if (isOpen && showCalendar) {
      const now = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 5);
      
      fetchScheduledEvents(now, end);
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        if (userData && userData.id) {
          setFormData(prev => ({
            ...prev,
            createdBy: userData.id
          }));
        }
      }
    }
  }, [isOpen, showCalendar]);

  useEffect(() => {
    // Actualizar la dirección cuando cambie la prop deliveryAddress
    setFormData(prev => ({
      ...prev,
      scheduleAdress: deliveryAddress || prev.scheduleAdress
    }));
  }, [deliveryAddress]);

  const fetchScheduledEvents = async (start, end) => {
    try {
      // Evitar llamadas duplicadas para el mismo rango
      const rangeKey = `${start.toISOString()}_${end.toISOString()}`;
      if (rangeKey === lastFetchedRange) {
        return;
      }
      setLastFetchedRange(rangeKey);

      // Formatear las fechas al formato ISO con zona horaria
      const startDate = start.toISOString();
      const endDate = end.toISOString();
      
      const response = await axiosInstance.get('purchase-order-schedules/range', {
        params: {
          startDate,
          endDate
        }
      });
      
      const formattedEvents = response.data.map(event => ({
        title: `${scheduleTypes.find(type => type.id === event.isDelivery)?.name || 'Agendamiento'} - ${event.appoimentNumber} - ${event.contactName} - ${event.orderNumber}`,
        start: event.scheduleDate,
        end: event.deliveryPickupDate,
        backgroundColor: event.isDelivery === 'DELIVERY' ? '#4aa1d8' : '#94d2bd',
        borderColor: event.isDelivery === 'DELIVERY' ? '#0070f2' : '#256f3a',
        extendedProps: {
          contactName: event.contactName,
          contactPhone: event.contactPhone,
          contactEmail: event.contactEmail,
          scheduleAddress: event.scheduleAddress
        }
      }));
      setEvents(formattedEvents);
    } catch (err) {
      console.error('Error al obtener los eventos:', err);
    }
  };

  const handleDatesSet = (dateInfo) => {
    // Solo actualizar eventos si estamos en la vista de calendario
    if (showCalendar) {
      fetchScheduledEvents(dateInfo.start, dateInfo.end);
    }
  };

  const handleDateSelect = (info) => {
    const now = new Date();
    const selectedDate = new Date(info.startStr);
    const deliveryDateObj = new Date(deliveryDate);

    if (selectedDate < now) {
      alert('No puedes seleccionar una fecha y hora pasada.');
      return;
    }

    // Guardar temporalmente el rango seleccionado
    const dateRange = {
      start: info.startStr,
      end: info.endStr
    };
    setSelectedDateRange(dateRange);

    // Comparar fechas
    if (selectedDate > deliveryDateObj) {
      setShowWarningDialog(true);
    } else {
      // Si la fecha es válida, actualizar directamente
      updateFormDates(dateRange);
    }
  };

  const updateFormDates = (dateRange) => {
    setFormData(prev => ({
      ...prev,
      scheduleDate: dateRange.start,
      deliveryPickupDate: dateRange.end
    }));
    setShowCalendar(false);
  };

  const handleWarningConfirm = () => {
    if (selectedDateRange) {
      updateFormDates(selectedDateRange);
    }
    setShowWarningDialog(false);
  };

  const handleWarningCancel = () => {
    setSelectedDateRange(null);
    setShowWarningDialog(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (!formData.contactName || !formData.contactPhone || !formData.scheduleDate || !formData.deliveryPickupDate || !formData.scheduleAdress) {
        setError('Por favor complete todos los campos requeridos');
        return;
      }

      const response = await axiosInstance.post('purchase-order-schedules', formData);

      if (response.data.code === 'SUCCESS') {
        setSuccess(true);
        setCreatedAppointmentNumber(response.data.data.appoimentNumber);
        setShowConfirmationDialog(true);
      } else {
        setError(response.data.message || 'Error al crear el agendamiento');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear el agendamiento');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationDialog(false);
    onClose();
    // Recargar la página para reflejar los cambios
    window.location.reload();
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return `${start.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })} - ${end.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onAfterClose={onClose}
        headerText={autoT("Agendar Entrega/Retiro")}
        style={{ width: '800px' }}
      >
        <div style={{ padding: '1rem' }}>
          {error && <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>{autoT(error)}</MessageStrip>}
          {success && <MessageStrip design="Success" style={{ marginBottom: '1rem' }}>{autoT("Agendamiento creado exitosamente")}</MessageStrip>}

          {showCalendar ? (
            <>
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1rem',
                padding: '0.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    backgroundColor: '#4aa1d8',
                    border: '2px solid #0070f2',
                    borderRadius: '4px'
                  }}></div>
                  <span>{autoT("Despacho")}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    backgroundColor: '#94d2bd',
                    border: '2px solid #256f3a',
                    borderRadius: '4px'
                  }}></div>
                  <span>{autoT("Retiro")}</span>
                </div>
              </div>

              <FullCalendar
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                selectable={true}
                events={events}
                slotMinTime="08:00:00"
                slotMaxTime="18:00:00"
                select={handleDateSelect}
                datesSet={handleDatesSet}
                height="400px"
                locale={esLocale}
                validRange={{
                  start: new Date().toISOString().split('T')[0]
                }}
                duration={{ days: 5 }}
                initialDate={new Date()}
                allDaySlot={false}
                slotDuration="01:00:00"
                expandRows={true}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                views={{
                  timeGridWeek: {
                    duration: { days: 5 }
                  }
                }}
                slotLabelFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }}
                eventContent={(eventInfo) => {
                  return (
                    <>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold' }}>{eventInfo.event.title}</div>
                      <div style={{ fontSize: '0.8em' }}>
                        {eventInfo.event.extendedProps.contactName} - {eventInfo.event.extendedProps.scheduleAddress}
                      </div>
                    </>
                  );
                }}
                slotLabelInterval="01:00:00"
                slotLabelClassNames="fc-slot-label"
                slotEventOverlap={false}
                nowIndicator={true}
                businessHours={{
                  daysOfWeek: [1, 2, 3, 4, 5],
                  startTime: '08:00',
                  endTime: '18:00',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button design="Transparent" onClick={onClose}>
                  {autoT("Cerrar")}
                </Button>
              </div>
            </>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1rem',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Número de orden:")}</Label>
                  <Input 
                    value={formData.orderNumber}
                    readOnly 
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Tipo de agendamiento:")}</Label>
                  <Select
                    value={formData.isDelivery}
                    onChange={(e) => setFormData({ ...formData, isDelivery: e.detail.selectedOption.value })}
                  >
                    {scheduleTypes.map(type => (
                      <Option key={type.id} value={type.id}>{autoT(type.name)}</Option>
                    ))}
                  </Select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Nombre de contacto:")}</Label>
                  <Input 
                    value={formData.contactName} 
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} 
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Teléfono de contacto:")}</Label>
                  <Input 
                    value={formData.contactPhone} 
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} 
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Fecha y hora seleccionada:")}</Label>
                  <Input 
                    value={formatDateRange(formData.scheduleDate, formData.deliveryPickupDate)}
                    readOnly 
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Email de contacto:")}</Label>
                  <Input 
                    value={formData.contactEmail} 
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} 
                    type="email"
                    placeholder={autoT("ejemplo@correo.com")}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Dirección de agendamiento:")}</Label>
                  <Input 
                    value={formData.scheduleAdress} 
                    onChange={(e) => setFormData({ ...formData, scheduleAdress: e.target.value })} 
                    placeholder={autoT("Ingrese la dirección completa")}
                    readonly={true}
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label>{autoT("Instrucciones especiales:")}</Label>
                  <TextArea
                    value={formData.specialInstructions}
                    onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                    placeholder={autoT("Ingrese instrucciones especiales si las hay")}
                    rows={3}
                  />
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '1rem', 
                marginTop: '1rem',
                gridColumn: 'span 2'
              }}>
                <Button design="Transparent" onClick={() => setShowCalendar(true)}>
                  {autoT("Volver al Calendario")}
                </Button>
                <Button design="Transparent" onClick={onClose}>
                  {autoT("Cancelar")}
                </Button>
                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? <BusyIndicator active size="Small" /> : autoT("Guardar")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={showConfirmationDialog}
        onAfterClose={handleConfirmationClose}
        headerText={autoT("Agendamiento Creado")}
        style={{ width: '400px' }}
      >
        <div style={{ padding: '1rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <Label>{autoT("Número de agendamiento:")}</Label>
            <Title level="H3" style={{ margin: '0.5rem 0' }}>{createdAppointmentNumber}</Title>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button onClick={handleConfirmationClose}>{autoT("Cerrar")}</Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={showWarningDialog}
        onAfterClose={() => setShowWarningDialog(false)}
        headerText={autoT("Advertencia de fecha")}
        style={{ width: '400px' }}
      >
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem', 
            marginBottom: '1rem',
            backgroundColor: '#fff3cd',
            padding: '1rem',
            borderRadius: '4px',
            border: '1px solid #ffeeba'
          }}>
            <Icon name="alert" style={{ color: '#856404', fontSize: '1.5rem' }} />
            <div>
              <Title level="H5" style={{ color: '#856404', marginBottom: '0.5rem' }}>
                {autoT("Fecha posterior a la entrega")}
              </Title>
              <Text style={{ color: '#856404' }}>
                {autoT("La fecha seleccionada es posterior a la fecha de entrega establecida en la orden ({{deliveryDate}}). ¿Está seguro de que desea continuar con esta fecha?", { deliveryDate: new Date(deliveryDate).toLocaleDateString('es-ES') })}
              </Text>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <Button design="Transparent" onClick={handleWarningCancel}>
              {autoT("Cancelar")}
            </Button>
            <Button onClick={handleWarningConfirm} design="Emphasized">
              {autoT("Continuar de todos modos")}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AppointmentModal;
